/**
 * PlanningContainer — owns the planning view mode (Calendario · Lista · Kanban
 * · Gantt), the date/range controls, the shared data (resources/services/
 * activities/items) and the create/edit modal. Each view is presentational.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';
import {
  plannerApi,
  type PlannerResource,
  type PlannerService,
  type PlannerActivity,
  type PlannerScheduledItem,
  type PlannerTaxonomies,
} from '@/services/api';
import { ymd, startOfWeek, startOfMonth, startOfYear, addDays } from './plannerShared';
import ScheduledForm, { type CreateCtx } from './ScheduledForm';
import PlanningCalendar from './PlanningCalendar';
import PlanningList from './PlanningList';
import PlanningKanban from './PlanningKanban';
import PlanningGantt from './PlanningGantt';

type ViewMode = 'calendar' | 'list' | 'kanban' | 'gantt';
type RangeMode = 'week' | 'month' | 'year';

const VIEWS: { id: ViewMode; label: string }[] = [
  { id: 'calendar', label: 'Calendario' },
  { id: 'list', label: 'Lista' },
  { id: 'kanban', label: 'Kanban' },
  { id: 'gantt', label: 'Gantt' },
];

export default function PlanningContainer({ tax, canManage }: { tax: PlannerTaxonomies | null; canManage: boolean }) {
  const [view, setView] = useState<ViewMode>('calendar');
  const [range, setRange] = useState<RangeMode>('week');
  const [anchor, setAnchor] = useState<Date>(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });

  const [resources, setResources] = useState<PlannerResource[]>([]);
  const [services, setServices] = useState<PlannerService[]>([]);
  const [activities, setActivities] = useState<PlannerActivity[]>([]);
  const [items, setItems] = useState<PlannerScheduledItem[]>([]);
  // Presence shifts in the same window — used only to detect when a work item
  // is assigned to an employee outside their planned shift.
  const [presenceShifts, setPresenceShifts] = useState<PlannerScheduledItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ item: PlannerScheduledItem | null; ctx: CreateCtx | null } | null>(null);

  // Window [from, to) for the current view.
  const { from, to } = useMemo(() => {
    if (view === 'calendar') {
      const f = new Date(anchor); f.setHours(0, 0, 0, 0);
      return { from: f, to: addDays(f, 1) };
    }
    if (range === 'week') {
      const f = startOfWeek(anchor);
      return { from: f, to: addDays(f, 7) };
    }
    if (range === 'year') {
      const f = startOfYear(anchor);
      return { from: f, to: new Date(f.getFullYear() + 1, 0, 1) };
    }
    const f = startOfMonth(anchor);
    return { from: f, to: new Date(f.getFullYear(), f.getMonth() + 1, 1) };
  }, [view, range, anchor]);

  const loadStatic = async () => {
    try {
      const [r, s, a] = await Promise.all([plannerApi.getResources(), plannerApi.getServices(), plannerApi.getActivities()]);
      setResources(r.data?.data || []);
      setServices((s.data?.data || []).filter((x: PlannerService) => x.active));
      setActivities(a.data?.data || []);
    } catch {
      toast.error('Error al cargar recursos/servicios');
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      // Fetch every item in the window in a single call and split locally,
      // so the views render work items but presence shifts are still available
      // for the coverage warning.
      const res = await plannerApi.getScheduled({ from: from.toISOString(), to: to.toISOString() });
      const all: PlannerScheduledItem[] = res.data?.data || [];
      setItems(all.filter((i) => (i.kind || 'work') === 'work'));
      setPresenceShifts(all.filter((i) => i.kind === 'presence'));
    } catch {
      toast.error('Error al cargar la planificación');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadStatic(); }, []);
  useEffect(() => { void loadItems(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [from, to]);

  const reload = () => { void loadItems(); };

  const commitMove = async (id: string, startISO: string, endISO: string) => {
    try {
      await plannerApi.updateScheduled(id, { start: startISO, end: endISO });
      reload();
    } catch {
      toast.error('Error al mover');
      reload();
    }
  };
  const changeState = async (id: string, stateNodeId: string | null) => {
    try {
      await plannerApi.updateScheduled(id, { stateNodeId });
      reload();
    } catch {
      toast.error('Error al cambiar estado');
      reload();
    }
  };

  const shift = (dir: number) => {
    if (view === 'calendar') setAnchor((a) => addDays(a, dir));
    else if (range === 'week') setAnchor((a) => addDays(a, dir * 7));
    else if (range === 'year') setAnchor((a) => new Date(a.getFullYear() + dir, 0, 1));
    else setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + dir, 1));
  };

  // Set of work items whose assigned employee has NO presence shift covering
  // the work interval. The views render these in amber + a warning label.
  // A resource is considered an employee when it has an employeeNif. Items on
  // non-employee resources never get flagged (no concept of "presence" there).
  const uncoveredItemIds = useMemo(() => {
    const empIds = new Set(resources.filter((r) => !!r.employeeNif).map((r) => r._id));
    const byResource = new Map<string, PlannerScheduledItem[]>();
    for (const p of presenceShifts) {
      const arr = byResource.get(p.resourceId) || [];
      arr.push(p);
      byResource.set(p.resourceId, arr);
    }
    const out = new Set<string>();
    for (const w of items) {
      if (!empIds.has(w.resourceId)) continue;
      const shifts = byResource.get(w.resourceId) || [];
      const ws = +new Date(w.start);
      const we = +new Date(w.end);
      const covered = shifts.some((p) => +new Date(p.start) <= ws && +new Date(p.end) >= we);
      if (!covered) out.add(w._id);
    }
    return out;
  }, [items, presenceShifts, resources]);

  const rangeLabel = () => {
    if (view === 'calendar') return anchor.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    if (range === 'week') return `${from.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })} – ${addDays(to, -1).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}`;
    if (range === 'year') return String(anchor.getFullYear());
    return anchor.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {VIEWS.map((v) => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${view === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
              {v.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronLeftIcon className="h-5 w-5" /></button>
          {view === 'calendar' ? (
            <input type="date" className="rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
              value={ymd(anchor)} onChange={(e) => { const [y, m, d] = e.target.value.split('-').map(Number); setAnchor(new Date(y, m - 1, d)); }} />
          ) : (
            <span className="px-2 text-sm font-medium text-gray-700 capitalize min-w-[10rem] text-center">{rangeLabel()}</span>
          )}
          <button onClick={() => shift(1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronRightIcon className="h-5 w-5" /></button>
          <button onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d); }} className="ml-1 px-2 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Hoy</button>
        </div>

        {view !== 'calendar' && (
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {(['week', 'month', 'year'] as RangeMode[]).map((rm) => (
              <button key={rm} onClick={() => setRange(rm)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${range === rm ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                {rm === 'week' ? 'Semana' : rm === 'month' ? 'Mes' : 'Año'}
              </button>
            ))}
          </div>
        )}

        {/* Global "Nuevo" button — works from any view (Calendar already has
            in-grid creation, but the other views had no entry point). */}
        {canManage && (
          <button
            onClick={() => {
              if (resources.length === 0) { toast.error('No hay recursos creados'); return; }
              const start = new Date();
              const min = start.getMinutes();
              const inc = min < 30 ? 30 - min : 60 - min; // next half hour
              start.setMinutes(min + inc, 0, 0);
              setModal({ item: null, ctx: { resourceId: resources[0]._id, start } });
            }}
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700">
            <PlusIcon className="h-4 w-4" /> Nuevo
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-gray-400"><LoadingSpinner size="lg" /></div>
      ) : (
        <>
          {view === 'calendar' && (
            <PlanningCalendar
              date={anchor} resources={resources} services={services} tax={tax} items={items} canManage={canManage}
              uncoveredItemIds={uncoveredItemIds}
              onCreate={(ctx) => setModal({ item: null, ctx })}
              onEdit={(item) => setModal({ item, ctx: null })}
              onCommitMove={commitMove}
            />
          )}
          {view === 'list' && (
            <PlanningList items={items} resources={resources} services={services} tax={tax}
              uncoveredItemIds={uncoveredItemIds}
              onEdit={(item) => setModal({ item, ctx: null })} />
          )}
          {view === 'kanban' && (
            <PlanningKanban items={items} resources={resources} services={services} tax={tax} canManage={canManage}
              uncoveredItemIds={uncoveredItemIds}
              onEdit={(item) => setModal({ item, ctx: null })} onChangeState={changeState} />
          )}
          {view === 'gantt' && (
            <PlanningGantt from={from} to={to} granularity={range === 'year' ? 'month' : 'day'}
              items={items} resources={resources} services={services} tax={tax}
              uncoveredItemIds={uncoveredItemIds}
              onEdit={(item) => setModal({ item, ctx: null })} />
          )}
        </>
      )}

      {modal && (
        <ScheduledForm
          tax={tax} activities={activities} services={services} resources={resources}
          initial={modal.item} createCtx={modal.ctx} canManage={canManage}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); reload(); }}
        />
      )}
    </div>
  );
}
